import { Building2, QrCode, UserRound, type LucideIcon } from 'lucide-react';
import { LEAD_ROUTES, PROPERTY_ROUTES, QR_ROUTES } from '@/api/constants/app.url';
import type { Property } from '@/modules/properties/types';
import type { Lead } from '@/modules/leads/types';
import type { QrCode as QrCodeRow } from '@/modules/qr-codes/types';

/** The entity families surfaced by the global (top-bar) search. */
export type SearchEntity = 'property' | 'lead' | 'qr_code';

/** A single, entity-agnostic search hit ready to render in the results panel. */
export interface SearchResult {
    entity:   SearchEntity;
    id:       string;
    /** Primary line — the entity's name/label. */
    title:    string;
    /** Secondary line — a few identifying details (id, city, phone…). */
    subtitle: string | null;
    /** Where selecting the result navigates. Leads have a detail page; the
     *  others deep-link to their list (mirrors notificationHref). */
    href:     string;
}

/** One entity's results, grouped for the sectioned dropdown. */
export interface SearchGroup {
    entity: SearchEntity;
    label:  string;
    icon:   LucideIcon;
    results: SearchResult[];
}

/** Query params for the fan-out search. */
export interface GlobalSearchParams {
    q:      string;
    /** Max hits to request per entity. Keeps the dropdown compact. */
    limit?: number;
}

/** Joins a set of detail fields into a subtitle, dropping empties. */
function subtitleOf(parts: Array<string | null | undefined>): string | null {
    const joined = parts.map((p) => p?.trim()).filter(Boolean).join(' · ');
    return joined || null;
}

export function propertyToResult(p: Property): SearchResult {
    return {
        entity:   'property',
        id:       p.id,
        title:    p.title,
        subtitle: subtitleOf([p.external_ref, p.city, p.region]),
        // Properties have no per-id route — the list is the honest destination.
        href:     PROPERTY_ROUTES.list,
    };
}

export function leadToResult(l: Lead): SearchResult {
    return {
        entity:   'lead',
        id:       l.id,
        title:    l.buyer_name || l.phone || l.email || 'Unnamed lead',
        subtitle: subtitleOf([l.phone, l.email, l.status_label]),
        href:     LEAD_ROUTES.detail.to(l.id),
    };
}

export function qrToResult(q: QrCodeRow): SearchResult {
    return {
        entity:   'qr_code',
        id:       q.id,
        title:    q.label,
        subtitle: subtitleOf([q.short_code, q.property_title || q.campaign_label || q.placement_label]),
        href:     QR_ROUTES.list,
    };
}

/** Visual + copy config for each group header. */
export const SEARCH_GROUP_META: Record<SearchEntity, { label: string; icon: LucideIcon }> = {
    property: { label: 'Properties', icon: Building2 },
    lead:     { label: 'Leads',      icon: UserRound },
    qr_code:  { label: 'QR codes',   icon: QrCode },
};
